import React from 'react';
import { find, get, noop } from 'lodash';
import pt from 'prop-types';

import Link from 'components/link';
import DateTime from 'components/date-time';
import SectionHeader from 'components/section-header';

import {
  formatCurrency,
  formatTableValue,
  template,
  imageUrl,
  classNames,
} from 'utils';
import { renderTemplate, truncatedText } from 'utils/collection';

function CollectionTable({
  title,
  titleCount = true,
  subtitle,
  fields,
  rows,
  action,
  emptyMessage,
  onClickRow,
  ...props
}) {
  const onClickRowInt = onClickRow || noop;
  const hasHeader = fields?.some((field) => Boolean(field?.label)) ?? false;
  const validRows = rows?.filter((row) => row != null) ?? [];

  return (
    <div className="view-body-section">
      <div className="view-body-subheader">
        <SectionHeader
          className="view-body-subtitle"
          title={title}
          subtitle={subtitle}
          count={titleCount ? validRows?.length : undefined}
        />
        {action && <div>{action}</div>}
      </div>
      <div className="view-section-content collection-full">
        {validRows?.length > 0 ? (
          <div className="collection-table-container">
            <table
              className={classNames('collection-table', {
                headless: !hasHeader,
              })}
            >
              {hasHeader && (
                <thead>
                  <tr>
                    {fields.reduce((acc, field, key) => {
                      if (field) {
                        acc.push(
                          <th key={field.label || key}>
                            {React.isValidElement(field) ? field : field.label}
                          </th>,
                        );
                      }

                      return acc;
                    }, [])}
                  </tr>
                </thead>
              )}

              <tbody>
                {validRows.map((record, index) => (
                  <tr key={record.id || index}>
                    {fields !== undefined ? (
                      fields.reduce((acc, field, key) => {
                        if (field) {
                          acc.push(
                            <CollectionRowField
                              {...props}
                              key={field.label || key}
                              id={key}
                              record={record}
                              field={field}
                              onClick={onClickRowInt}
                              index={index}
                            />,
                          );
                        }

                        return acc;
                      }, [])
                    ) : (
                      <CollectionRowField
                        {...props}
                        record={record}
                        field={{}}
                        onClick={onClickRowInt}
                        index={index}
                      />
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="collection-none-found">
            {emptyMessage || `No ${title.toLowerCase()} yet`}
          </div>
        )}
      </div>
    </div>
  );
}

CollectionTable.propTypes = {
  title: pt.string,
  titleCount: pt.bool,
  subtitle: pt.string,
  fields: pt.array,
  rows: pt.array,
  action: pt.func,
  emptyMessage: pt.string,
  onClickRow: pt.func,
};

export default React.memo(CollectionTable);

export class CollectionRowField extends React.PureComponent {
  state = { value: undefined };

  componentDidMount() {
    this.setValueState();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.field !== this.props.field ||
      prevProps.record !== this.props.record ||
      prevProps.disabledRecords !== this.props.disabledRecords
    ) {
      this.setValueState();
    }
  }

  setValueState() {
    const { field } = this.props;

    if (field.template) {
      this.getValueFromRecordTemplate(this.props).then((value) =>
        this.setState({ value }),
      );
    } else {
      const value = this.getValueFromRecord(this.props);
      this.setState({ value });
    }
  }

  getValueFromRecord({ id, field, record, ...props }) {
    let value;

    if (id === undefined) {
      return record;
    }

    if (field.func) {
      value = field.func(record, props);
    } else if (field.path) {
      value = get(record, field.path);
    } else {
      value = record?.[id];
    }

    if (field.options) {
      const optionValue = find(field.options, { value });
      if (optionValue?.label) {
        value = optionValue.label;
      }
    }

    return value || null;
  }

  // eslint-disable-next-line require-await
  async getValueFromRecordTemplate({ field, record }) {
    if (field.template) {
      return renderTemplate(field.template, record);
    }
  }

  onRowClick = (event) => {
    const { record, onClick } = this.props;

    return onClick(event, record);
  };

  renderStringValue(value) {
    return React.isValidElement(value)
      ? value
      : formatTableValue(truncatedText(value, 96));
  }

  render() {
    const { field, record, index } = this.props;
    const { value } = this.state;

    if (value === undefined) {
      return null;
    }

    const colProps = {
      'data-index': index,
      onClick: this.onRowClick,
      className: classNames(
        `${field.template ? 'string' : field.type}`,
        field.className,
        {
          right: field.align === 'right',
          'tight-right': field.tightRight,
        },
      ),
      width: field.width,
    };

    switch (field.type || 'string') {
      case 'link': {
        const url = fieldLinkUrl(field.url, record);
        return (
          <td {...colProps} data-url={url}>
            {React.isValidElement(value)
              ? value
              : formatTableValue(
                  value && (
                    <Link className="collection-table-link" to={url}>
                      {truncatedText(value, 96)}
                    </Link>
                  ),
                )}
          </td>
        );
      }

      case 'date':
        colProps.className += ' nowrap';
        return (
          <td {...colProps}>
            {value ? (
              <DateTime value={value} format={field.format || 'age'} />
            ) : (
              <span className="muted">&mdash;</span>
            )}
          </td>
        );

      case 'currency':
        colProps.className += ' currency';
        return (
          <td {...colProps}>
            {field.func
              ? value
              : formatCurrency(
                  value,
                  record.currency,
                  field.rounded ? { precision: undefined } : undefined,
                )}
          </td>
        );

      case 'image': {
        const imageValue = value && (value[0] || value);

        const image = imageValue && (
          <span className="image">
            {field.func
              ? value
              : imageValue && (
                  <img
                    title={imageValue.caption || ''}
                    alt={imageValue.caption || ''}
                    src={imageUrl(imageValue, {
                      width: 90,
                      height: 90,
                      padded: true,
                    })}
                  />
                )}
          </span>
        );

        colProps.className += ' image';
        return (
          <td {...colProps}>
            <span className="collection-table-images">
              {image ? (
                field.url ? (
                  <Link to={fieldLinkUrl(field.url, record)}>{image}</Link>
                ) : (
                  image
                )
              ) : (
                <span className="image">&nbsp;</span>
              )}
            </span>
          </td>
        );
      }

      case 'order-images':
        return (
          <td {...colProps}>
            <span className="collection-table-images">
              {Array.isArray(value) &&
                value.map(
                  (item, index) =>
                    item.product &&
                    (index < 4 ? (
                      <span key={index} className="image">
                        <img
                          title={item.name || item.product.name}
                          alt={item.name || item.product.name}
                          src={imageUrl(item.product || item, {
                            width: 45,
                            height: 45,
                            padded: true,
                          })}
                        />
                      </span>
                    ) : index === 4 ? (
                      <span key={index} className="image-overflow">
                        ...
                      </span>
                    ) : null),
                )}
            </span>
          </td>
        );

      case 'tags': {
        const tags = Array.isArray(value) ? value : [];
        return (
          <td {...colProps}>
            {tags.length > 0 ? (
              <ul className="view-body-tags">
                {tags.map((tag, i) => (
                  <li key={i} className="nowrap">
                    {tag}
                  </li>
                ))}
              </ul>
            ) : (
              formatTableValue(null)
            )}
          </td>
        );
      }

      case 'string':
        return (
          <td {...colProps}>
            {Array.isArray(value)
              ? value.map(this.renderStringValue)
              : this.renderStringValue(value)}
          </td>
        );

      default:
        return <td {...colProps}>{formatTableValue()}</td>;
    }
  }
}

export function fieldLinkUrl(fieldUrl, record) {
  if (typeof fieldUrl === 'string') {
    return template(fieldUrl, record);
  }

  if (typeof fieldUrl === 'function') {
    return fieldUrl(record);
  }

  return fieldUrl;
}
